import { AxiosError } from 'axios';
import { AuthService } from '@vue-altoleap-libraries/vue-altoleap-accounts-lib';
import { axios } from '@/plugins/axios';
import { MicrosoftAuthService } from '@/services/microsoft-auth';
import { SystemError } from '@/models/error/error';
import {
  IMicrosoftAuthActions,
  IMicrosoftCallbackPayload,
  MicrosoftAuthContext
} from './actions.types';

const MICROSOFT_CLIENT_ID = process.env.VUE_APP_MICROSOFT_CLIENT_ID as string;
const MICROSOFT_TENANT_ID = process.env.VUE_APP_MICROSOFT_TENANT_ID as string;
const MICROSOFT_REDIRECT_URI = process.env
  .VUE_APP_MICROSOFT_CALLBACK_URL as string;

const microsoftAuthService = new MicrosoftAuthService(axios);
const authService = new AuthService(axios);

export const actions: IMicrosoftAuthActions = {
  authorizeMicrosoft(_context: MicrosoftAuthContext): void {
    const stateParam =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : Array.from(crypto.getRandomValues(new Uint8Array(16)))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
    sessionStorage.setItem('microsoft_state', stateParam);

    const params = new URLSearchParams({
      client_id: MICROSOFT_CLIENT_ID,
      response_type: 'code',
      redirect_uri: MICROSOFT_REDIRECT_URI,
      response_mode: 'query',
      // Intentionally omit `offline_access`: backend issues app JWT refresh tokens
      // from /v1/auth/microsoft/ independently of Microsoft refresh-token scope.
      // Adding offline_access would not change portal session refresh behaviour here.
      scope: 'openid profile email',
      state: stateParam
    });

    window.location.href = `https://login.microsoftonline.com/${MICROSOFT_TENANT_ID}/oauth2/v2.0/authorize?${params}`;
  },

  async handleMicrosoftCallback(
    context: MicrosoftAuthContext,
    payload: IMicrosoftCallbackPayload
  ): Promise<void> {
    const storedState = sessionStorage.getItem('microsoft_state');

    if (payload.state !== storedState) {
      throw new SystemError({
        name: 'MicrosoftAuthError',
        message: 'Invalid state parameter. Please try signing in again.',
        detail: 'OAuth state mismatch'
      });
    }

    let data;
    try {
      data = await microsoftAuthService.authorize(
        payload.code,
        MICROSOFT_REDIRECT_URI
      );
    } catch (err) {
      const error = err as AxiosError;
      if (error.response) {
        throw new SystemError({
          name: 'MicrosoftAuthError',
          message: error.message ?? 'Microsoft authorization failed',
          detail: error.response.data?.detail ?? '',
          statusCode: error.response.status
        });
      }
      throw err;
    }

    localStorage.setItem('access__token', data.access);
    if (data.refresh) {
      localStorage.setItem('refresh__token', data.refresh);
      context.dispatch('auth/setRefreshToken', data.refresh, { root: true });
    } else {
      localStorage.removeItem('refresh__token');
      context.dispatch('auth/setRefreshToken', '', { root: true });
    }
    sessionStorage.removeItem('microsoft_state');

    context.dispatch('auth/setAccessToken', data.access, { root: true });
    axios.defaults.headers.common['Authorization'] = `JWT ${data.access}`;

    let currentUser = data.user;
    try {
      const hydratedUser = await authService.fetchMe();
      if (hydratedUser && hydratedUser.id) {
        currentUser = hydratedUser;
      }
    } catch {
      // Keep callback success non-blocking: if profile hydration fails, continue
      // with the callback user payload so sign-in still completes.
    }

    // Keep auth Vuex module and localStorage in sync during the same SPA session.
    // auth/isLoggedin does JSON.parse(state.user), so state.user must be serialized.
    // In this app stack, localStorage.user is a profile object (not JWT payload). The
    // Altoleap AuthGuard evaluates tokenService.isExpired(parsedUser.exp); when exp is
    // missing this resolves truthy due to NaN comparison semantics, and access__token
    // presence remains the effective auth gate. This mirrors legacy login behaviour.
    const serializedUser = JSON.stringify(currentUser);
    localStorage.setItem('user', serializedUser);
    context.commit('auth/SET_USER', serializedUser, { root: true });
    context.commit('auth/SET_CURRENT_USER', currentUser, { root: true });
  }
};
